import { supabase } from './supabase';

const TRUST_DURATION_DAYS = 30;
const REMEMBER_DEVICE_KEY = 'xmanage_remember_device';

export interface TrustedDevice {
  id: string;
  user_id: string;
  device_fingerprint: string;
  device_name: string;
  expires_at: string;
  created_at: string;
}

export function generateDeviceFingerprint(): string {
  const ua = navigator.userAgent;
  const screen = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const raw = `${ua}|${screen}|${timezone}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw.charCodeAt(i);
    hash = ((hash << 5) - hash) + ch;
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

export function getDeviceName(): string {
  const ua = navigator.userAgent;
  let browser = 'Browser';
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';

  let os = 'Unknown OS';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return `${browser} on ${os}`;
}

export async function isDeviceTrusted(userId: string): Promise<boolean> {
  const fingerprint = generateDeviceFingerprint();

  const { data, error } = await supabase
    .from('trusted_devices')
    .select('id')
    .eq('user_id', userId)
    .eq('device_fingerprint', fingerprint)
    .gt('expires_at', new Date().toISOString())
    .maybeSingle();

  if (error) return false;
  return !!data;
}

export async function trustCurrentDevice(userId: string): Promise<TrustedDevice> {
  const fingerprint = generateDeviceFingerprint();
  const deviceName = getDeviceName();
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + TRUST_DURATION_DAYS);

  await supabase
    .from('trusted_devices')
    .delete()
    .eq('user_id', userId)
    .eq('device_fingerprint', fingerprint);

  const { data, error } = await supabase
    .from('trusted_devices')
    .insert({
      user_id: userId,
      device_fingerprint: fingerprint,
      device_name: deviceName,
      expires_at: expiresAt.toISOString(),
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getTrustedDevices(userId: string): Promise<TrustedDevice[]> {
  const { data, error } = await supabase
    .from('trusted_devices')
    .select('*')
    .eq('user_id', userId)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function removeTrustedDevice(deviceId: string): Promise<void> {
  const { error } = await supabase
    .from('trusted_devices')
    .delete()
    .eq('id', deviceId);

  if (error) throw error;
}

export async function removeAllTrustedDevices(userId: string): Promise<void> {
  const { error } = await supabase
    .from('trusted_devices')
    .delete()
    .eq('user_id', userId);

  if (error) throw error;
}

export function setRememberDeviceFlag(value: boolean): void {
  if (value) {
    sessionStorage.setItem(REMEMBER_DEVICE_KEY, '1');
  } else {
    sessionStorage.removeItem(REMEMBER_DEVICE_KEY);
  }
}

export function getRememberDeviceFlag(): boolean {
  return sessionStorage.getItem(REMEMBER_DEVICE_KEY) === '1';
}

export function clearRememberDeviceFlag(): void {
  sessionStorage.removeItem(REMEMBER_DEVICE_KEY);
}

const SESSION_VERIFIED_KEY = 'xmanage_session_verified';
const SESSION_VERIFIED_DURATION_MS = 24 * 60 * 60 * 1000;

export function markSessionVerified(userId: string): void {
  localStorage.setItem(SESSION_VERIFIED_KEY, JSON.stringify({ userId, ts: Date.now() }));
}

export function isSessionVerified(userId: string): boolean {
  try {
    const raw = localStorage.getItem(SESSION_VERIFIED_KEY);
    if (!raw) return false;
    const { userId: storedId, ts } = JSON.parse(raw);
    if (storedId !== userId) return false;
    return Date.now() - ts < SESSION_VERIFIED_DURATION_MS;
  } catch {
    return false;
  }
}

export function clearSessionVerified(): void {
  localStorage.removeItem(SESSION_VERIFIED_KEY);
}
